import { MoreHorizontal } from "lucide-react";
import type { ComponentType } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export interface ActionItem {
  /** Icon component, typically from lucide-react. */
  icon: ComponentType<{ className?: string }>;
  /** Menu item label. */
  label: string;
  onClick: () => void;
  /** Visual variant. `"destructive"` tints the item red. */
  variant?: "default" | "destructive";
  disabled?: boolean;
  /** Render a separator above this item. Use to group destructive actions at the bottom. */
  separatorBefore?: boolean;
}

interface ActionCellProps {
  items: ActionItem[];
  /** Disable the entire menu trigger (e.g. for the signed-in user's own row). */
  disabled?: boolean;
  /** Visual label for screen readers. Defaults to "Open actions". */
  triggerLabel?: string;
  className?: string;
}

/**
 * Standard row-actions dropdown — a `MoreHorizontal` trigger with a fixed-style
 * menu of items. Used at the right edge of every list row that needs per-record
 * actions. Items render in array order; pass `separatorBefore: true` to inject
 * a divider before the item (e.g. between safe and destructive actions).
 */
export function ActionCell({
  items,
  disabled,
  triggerLabel = "Open actions",
  className,
}: ActionCellProps) {
  if (items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" disabled={disabled} className={className}>
          <span className="sr-only">{triggerLabel}</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((item, idx) => {
          const Icon = item.icon;
          return (
            <ActionEntry key={`${item.label}-${idx}`} separatorBefore={item.separatorBefore}>
              <DropdownMenuItem
                onClick={item.onClick}
                disabled={item.disabled}
                className={cn(
                  item.variant === "destructive" && "text-destructive focus:text-destructive",
                )}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </DropdownMenuItem>
            </ActionEntry>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ActionEntry({
  separatorBefore,
  children,
}: {
  separatorBefore?: boolean;
  children: React.ReactNode;
}) {
  if (separatorBefore) {
    return (
      <>
        <DropdownMenuSeparator />
        {children}
      </>
    );
  }
  return <>{children}</>;
}
